import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

import FloatingAdminMenu from "./FloatMenu";
import AtomBackground from "./Atombackground";

import {
  FiLock, FiEye, FiEyeOff, FiLogOut,
  FiSave, FiRefreshCw, FiCheck, FiAlertCircle,
  FiChevronRight, FiChevronLeft, FiArrowRight,
  FiMenu, FiX,
} from "react-icons/fi";

/* ─────────────────────────────────────
   API & AUTH
───────────────────────────────────── */
const API      = "https://medicinacondante.com/bot.php";
const PASSWORD = "dante.admin";

const initialForm = {
  nombre: "", descripcion: "", personalidad: "", tono: "",
  mensaje_central: "", reglas: "", estructura: "", informacion: "",
  modelo: "gpt-4o-mini", temperatura: 0.5, max_tokens: 120, activo: 1,
};

/* ─────────────────────────────────────
   ESTILOS GLOBALES
───────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;1,300&display=swap');

    :root {
      --gold:      #C9A84C;
      --gold-dim:  rgba(201,168,76,0.55);
      --gold-sub:  rgba(201,168,76,0.08);
      --gold-brd:  rgba(201,168,76,0.20);
      --bg:        #0A0906;
      --bg-2:      #0F0E0B;
      --bg-3:      #141208;
      --line:      rgba(201,168,76,0.12);
      --ink:       #EDE0C8;
      --ink-2:     rgba(237,224,200,0.62);
      --ink-3:     rgba(237,224,200,0.32);
      --green:     #6DBF8F;
      --red:       #E07A7A;
    }

    *, *::before, *::after { box-sizing: border-box; }

    body {
      background: var(--bg);
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    ::placeholder {
      color: var(--ink-3);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 300;
      font-style: italic;
    }

    textarea, input, select {
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    @keyframes adm-spin {
      to { transform: rotate(360deg); }
    }
    .adm-spin { animation: adm-spin 0.9s linear infinite; }

    @keyframes adm-pulse-dot {
      0%, 100% { opacity: 0.45; transform: scale(1); }
      50%       { opacity: 1;   transform: scale(1.4); }
    }
    .adm-pulse { animation: adm-pulse-dot 2s ease-in-out infinite; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg-2); }
    ::-webkit-scrollbar-thumb { background: var(--gold-brd); border-radius: 2px; }

    @media (min-width: 1280px) {
      .adm-sidebar        { display: flex !important; }
      .show-mobile-header { display: none !important; }
    }
    @media (max-width: 1279px) {
      .adm-sidebar    { display: none !important; }
      .hide-on-mobile { display: none !important; }
    }
  `}</style>
);

/* ─────────────────────────────────────
   STEPS
───────────────────────────────────── */
const STEPS = [
  {
    key: "nombre", label: "Nombre del agente",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="7" stroke="var(--gold)" strokeWidth="1"/>
        <circle cx="10" cy="10" r="3" stroke="var(--gold)" strokeWidth="1"/>
        <line x1="10" y1="3"  x2="10" y2="1"  stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="10" y1="17" x2="10" y2="19" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="3"  y1="10" x2="1"  y2="10" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="17" y1="10" x2="19" y2="10" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
      </svg>
    ),
    required: true,
    hint: "El nombre con el que se identificará el asistente. Debe ser coherente con la identidad de IDM Medicina — claro, profesional y memorable.",
    placeholder: "Ej: Dante, Asistente IDM, Consultor Médico IDM...",
    rows: 1, type: "input",
  },
  {
    key: "descripcion", label: "Descripción general",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="3" y="2" width="14" height="16" rx="1" stroke="var(--gold)" strokeWidth="1"/>
        <line x1="6" y1="7"  x2="14" y2="7"  stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="6" y1="10" x2="14" y2="10" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="6" y1="13" x2="10" y2="13" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
      </svg>
    ),
    required: true,
    hint: "Una descripción breve del rol del agente: a quién asiste (estudiantes de medicina, aspirantes al MIR, alumnos de cursos IDM) y cuál es su función principal.",
    placeholder: "Ej: Soy el asistente virtual de IDM Medicina con Dante. Ayudo a estudiantes de medicina a resolver dudas sobre cursos, mentorías y programas disponibles. Mi misión es guiar cada consulta hacia el camino de aprendizaje correcto.",
    rows: 4, type: "textarea",
  },
  {
    key: "personalidad", label: "Personalidad",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="7" r="4" stroke="var(--gold)" strokeWidth="1"/>
        <path d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
      </svg>
    ),
    required: true,
    hint: "Define cómo se expresa el agente. IDM tiene una identidad clara: docente apasionado, sin condescendencia, que cree que cualquier persona puede entender medicina si se le explica bien.",
    placeholder: "Ej: Soy directo, cercano y apasionado por la enseñanza médica. No trato a los estudiantes como incapaces — creo que el problema nunca es la inteligencia, sino la manera en que se enseña. Genero confianza desde el primer mensaje y siempre invito a seguir aprendiendo.",
    rows: 5, type: "textarea",
  },
  {
    key: "tono", label: "Tono de comunicación",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 4h14a1 1 0 011 1v8a1 1 0 01-1 1H5l-3 3V5a1 1 0 011-1z" stroke="var(--gold)" strokeWidth="1"/>
        <line x1="7" y1="8"  x2="13" y2="8"  stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="7" y1="11" x2="11" y2="11" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
      </svg>
    ),
    required: true,
    hint: "El estilo de escritura del agente. IDM combina profesionalismo médico con una comunicación accesible — sin jerga innecesaria, sin frialdad clínica.",
    placeholder: "Ej: Profesional pero humano. Evito tecnicismos innecesarios cuando hablo de los cursos y servicios. No soy ni demasiado informal ni distante. Uso oraciones cortas, claras y con énfasis en la comprensión real.",
    rows: 4, type: "textarea",
  },
  {
    key: "mensaje_central", label: "Mensaje central",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <polygon points="10,2 12.5,8 19,8 13.5,12 15.5,18 10,14 4.5,18 6.5,12 1,8 7.5,8" stroke="var(--gold)" strokeWidth="1" fill="none" strokeLinejoin="round"/>
      </svg>
    ),
    required: true,
    hint: "La propuesta de valor central que el agente debe transmitir siempre. Para IDM, esto es la metodología pedagógica única: comprensión real, no memoria mecánica.",
    placeholder: "Ej: En IDM Medicina creemos que entender medicina es posible para todos. No se trata de memorizar diapositivas — se trata de razonar, comprender y aplicar. Eso es lo que hacemos con más de 210k estudiantes en toda Latinoamérica.",
    rows: 4, type: "textarea",
  },
  {
    key: "reglas", label: "Reglas de conducta",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="3" width="16" height="14" rx="1.5" stroke="var(--gold)" strokeWidth="1"/>
        <line x1="2" y1="7" x2="18" y2="7" stroke="var(--gold)" strokeWidth="1"/>
        <line x1="6" y1="1" x2="6"  y2="5" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="14" y1="1" x2="14" y2="5" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <rect x="6"  y="10" width="3" height="3" rx="0.5" fill="var(--gold)" opacity="0.6"/>
        <rect x="11" y="10" width="3" height="3" rx="0.5" stroke="var(--gold)" strokeWidth="0.8" opacity="0.4"/>
      </svg>
    ),
    required: true,
    hint: "Límites claros del agente. Qué puede y qué no puede hacer. Incluye cómo derivar consultas complejas a WhatsApp o al equipo de IDM.",
    placeholder: "Ej: No debo dar diagnósticos médicos ni reemplazar la consulta clínica. No confirmo precios exactos sin derivar al equipo. No hablo de temas ajenos a los programas IDM. Siempre derivo al WhatsApp de IDM si la consulta requiere atención personalizada.",
    rows: 5, type: "textarea",
  },
  {
    key: "estructura", label: "Estructura de respuesta",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <line x1="4" y1="5"  x2="16" y2="5"  stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="4" y1="9"  x2="16" y2="9"  stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <line x1="4" y1="13" x2="10" y2="13" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <circle cx="15" cy="15" r="3" stroke="var(--gold)" strokeWidth="1"/>
        <line x1="15" y1="13.5" x2="15" y2="15" stroke="var(--gold)" strokeWidth="1" strokeLinecap="round"/>
        <circle cx="15" cy="16" r="0.5" fill="var(--gold)"/>
      </svg>
    ),
    required: false,
    hint: "Opcional. Cómo debe formatear y organizar sus respuestas. Ideal para mantener consistencia en todos los canales donde IDM despliega el bot.",
    placeholder: "Ej: Respuestas de 2 a 4 oraciones por defecto. Si la consulta es sobre un curso específico, menciono nombre, beneficio clave y CTA a Hotmart. Siempre termino con una pregunta o una invitación a continuar la conversación.",
    rows: 4, type: "textarea",
  },
  {
    key: "informacion", label: "Información contextual",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="8" stroke="var(--gold)" strokeWidth="1"/>
        <line x1="10" y1="9" x2="10" y2="14" stroke="var(--gold)" strokeWidth="1.2" strokeLinecap="round"/>
        <circle cx="10" cy="6.5" r="0.8" fill="var(--gold)"/>
      </svg>
    ),
    required: false,
    hint: "Opcional. Datos concretos sobre IDM: cursos activos, precios, plataformas, canales de contacto, redes sociales, comunidad.",
    placeholder: "Ej: IDM Medicina tiene sede en Mar del Plata, Argentina. Contamos con 6 programas activos en Hotmart. La comunidad supera los 210k seguidores en Instagram y TikTok. WhatsApp de contacto: +54 9 223 XXX-XXXX. Todos los cursos incluyen acceso de por vida y soporte por grupo de alumnos.",
    rows: 6, type: "textarea",
  },
];

/* ─────────────────────────────────────
   MOTION VARIANTS
───────────────────────────────────── */
const pageVariants = {
  enter:  (dir) => ({ opacity: 0, x: dir > 0 ? 48 : -48, scale: 0.98 }),
  center: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.44, ease: [0.22, 1, 0.36, 1] } },
  exit:   (dir) => ({ opacity: 0, x: dir < 0 ? 48 : -48, scale: 0.98, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

/* ─────────────────────────────────────
   SECTION LABEL
───────────────────────────────────── */
const SectionLabel = ({ children, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className="inline-block w-5 h-px" style={{ background: "var(--gold)", opacity: 0.7 }} />
    <span style={{ fontWeight: 700, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-3)" }}>
      {children}
    </span>
  </div>
);

/* ─────────────────────────────────────
   STYLED FIELD
───────────────────────────────────── */
const StyledField = ({ value, onChange, name, placeholder, rows, type }) => {
  const [focused, setFocused] = useState(false);

  const base = {
    background:   "var(--bg-2)",
    color:        "var(--ink)",
    border:       `1px solid ${focused ? "rgba(201,168,76,0.6)" : "var(--line)"}`,
    outline:      "none",
    width:        "100%",
    fontFamily:   "'Plus Jakarta Sans', sans-serif",
    fontWeight:   300,
    fontSize:     14,
    lineHeight:   1.8,
    transition:   "border-color 0.25s, box-shadow 0.25s",
    boxShadow:    focused ? "0 0 0 4px rgba(201,168,76,0.05), inset 0 1px 3px rgba(0,0,0,0.3)" : "inset 0 1px 3px rgba(0,0,0,0.2)",
    borderRadius: 0,
  };

  if (type === "input") {
    return (
      <div className="relative">
        <input
          name={name} value={value || ""} onChange={onChange}
          placeholder={placeholder} type="text"
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ ...base, height: 48, padding: "0 16px" }}
        />
        <AnimatePresence>
          {focused && (
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }} exit={{ scaleX: 0, opacity: 0 }}
              className="absolute bottom-0 left-0 right-0 h-px origin-left"
              style={{ background: "linear-gradient(90deg, var(--gold), transparent)" }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="relative">
      <textarea
        name={name} value={value || ""} onChange={onChange}
        placeholder={placeholder} rows={rows}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ ...base, resize: "vertical", minHeight: 80, padding: "12px 16px" }}
      />
      <AnimatePresence>
        {focused && (
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }} exit={{ scaleX: 0, opacity: 0 }}
            className="absolute bottom-0 left-0 right-0 h-px origin-left"
            style={{ background: "linear-gradient(90deg, var(--gold), transparent)" }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─────────────────────────────────────
   PROGRESS BAR
───────────────────────────────────── */
const ProgressBar = ({ current, total }) => (
  <div className="relative w-full h-px" style={{ background: "var(--line)" }}>
    <motion.div
      className="absolute left-0 top-0 h-full"
      style={{ background: "linear-gradient(90deg, rgba(201,168,76,0.4), var(--gold))" }}
      animate={{ width: `${((current + 1) / total) * 100}%` }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    />
    <motion.div
      className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
      style={{ background: "var(--gold)", boxShadow: "0 0 8px rgba(201,168,76,0.6)" }}
      animate={{ left: `calc(${((current + 1) / total) * 100}% - 3px)` }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    />
  </div>
);

/* ─────────────────────────────────────
   SIDEBAR DESKTOP
───────────────────────────────────── */
const StepSidebar = ({ steps, current, completed, onJump }) => (
  <div className="adm-sidebar" style={{ flexDirection: "column", width: 220, flexShrink: 0, borderTop: "1px solid var(--line)" }}>
    {steps.map((s, i) => {
      const done   = completed.has(s.key);
      const active = i === current;
      return (
        <motion.button
          key={s.key} onClick={() => onJump(i)}
          whileHover={{ paddingLeft: active ? 12 : 6 }}
          className="flex items-center gap-3 text-left"
          style={{
            padding:      `14px 0 14px ${active ? "12px" : "0px"}`,
            background:   "transparent",
            border:       "none",
            borderBottom: "1px solid var(--line)",
            borderLeft:   `2px solid ${active ? "var(--gold)" : done ? "rgba(201,168,76,0.22)" : "transparent"}`,
            cursor:       "pointer",
            transition:   "border-color 0.2s, padding 0.2s",
          }}
        >
          <div style={{
            width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            border:     `1px solid ${done ? "var(--gold)" : active ? "rgba(201,168,76,0.5)" : "var(--line)"}`,
            background: done ? "var(--gold)" : "transparent",
            color:      done ? "#0A0906" : active ? "var(--gold)" : "var(--ink-3)",
            fontSize: 9, fontWeight: 700,
          }}>
            {done ? <FiCheck size={9} /> : i + 1}
          </div>
          <div className="overflow-hidden min-w-0">
            <p style={{ fontSize: 11, fontWeight: active ? 700 : 300, color: active ? "var(--gold)" : done ? "var(--ink-2)" : "var(--ink-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {s.label}
            </p>
            {!s.required && (
              <p style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-3)" }}>
                Opcional
              </p>
            )}
          </div>
        </motion.button>
      );
    })}
  </div>
);

/* ─────────────────────────────────────
   DRAWER MOBILE
───────────────────────────────────── */
const MobileStepDrawer = ({ steps, current, completed, onJump, open, onClose }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-40"
          style={{ background: "rgba(0,0,0,0.82)", backdropFilter: "blur(8px)" }}
        />
        <motion.div
          initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
          transition={{ type: "spring", stiffness: 260, damping: 30, mass: 0.9 }}
          className="fixed left-0 top-0 bottom-0 z-50 flex flex-col w-72 shadow-2xl"
          style={{ background: "var(--bg-3)", borderRight: "1px solid var(--line)" }}
        >
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--line)" }}>
            <SectionLabel>Pasos del wizard</SectionLabel>
            <motion.button
              whileHover={{ rotate: 90 }} whileTap={{ scale: 0.88 }}
              transition={{ duration: 0.18 }}
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center"
              style={{ border: "1px solid var(--line)", color: "var(--gold)", background: "var(--gold-sub)", cursor: "pointer" }}
            >
              <FiX size={13} />
            </motion.button>
          </div>
          <div className="flex-1 overflow-y-auto" style={{ padding: "8px 20px" }}>
            {steps.map((s, i) => {
              const done   = completed.has(s.key);
              const active = i === current;
              return (
                <motion.button
                  key={s.key} whileTap={{ scale: 0.97 }}
                  onClick={() => { onJump(i); onClose(); }}
                  className="w-full flex items-center gap-3 text-left"
                  style={{
                    padding:      `14px 0 14px ${active ? "10px" : "0px"}`,
                    background:   "transparent",
                    border:       "none",
                    borderBottom: "1px solid var(--line)",
                    borderLeft:   `2px solid ${active ? "var(--gold)" : done ? "rgba(201,168,76,0.22)" : "transparent"}`,
                    cursor:       "pointer",
                    transition:   "padding 0.2s",
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border:     `1px solid ${done ? "var(--gold)" : active ? "rgba(201,168,76,0.5)" : "var(--line)"}`,
                    background: done ? "var(--gold)" : "transparent",
                    color:      done ? "#0A0906" : active ? "var(--gold)" : "var(--ink-3)",
                    fontSize: 9, fontWeight: 700,
                  }}>
                    {done ? <FiCheck size={10} /> : i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 12, fontWeight: active ? 700 : 300, color: active ? "var(--gold)" : done ? "var(--ink-2)" : "var(--ink-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {s.label}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {!s.required && <span style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-3)" }}>Opcional</span>}
                      {done && <span style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--green)" }}>✓ Completado</span>}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

/* ─────────────────────────────────────
   ACTIVE TOGGLE
───────────────────────────────────── */
const ActiveToggle = ({ value, onChange }) => (
  <div className="flex items-center justify-between px-5 py-4" style={{ background: "var(--bg-3)", border: "1px solid var(--line)" }}>
    <div className="flex-1 min-w-0 mr-4">
      <SectionLabel>Estado del agente</SectionLabel>
      <p className="mt-1 truncate" style={{ fontSize: 12, fontWeight: 300, color: value ? "var(--green)" : "var(--ink-3)" }}>
        {value ? "Activo — respondiendo consultas en tiempo real" : "Pausado — el bot no responde"}
      </p>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <div
        className={value ? "adm-pulse" : ""}
        style={{ width: 7, height: 7, borderRadius: "50%", background: value ? "var(--green)" : "var(--ink-3)", opacity: value ? 1 : 0.35 }}
      />
      <button
        onClick={onChange}
        className="relative flex-shrink-0"
        style={{ width: 44, height: 24, background: value ? "rgba(109,191,143,0.18)" : "var(--gold-sub)", border: `1px solid ${value ? "rgba(109,191,143,0.35)" : "var(--line)"}`, cursor: "pointer", borderRadius: 0 }}
      >
        <motion.div
          animate={{ x: value ? 22 : 2 }}
          transition={{ type: "spring", stiffness: 500, damping: 32 }}
          style={{ position: "absolute", top: 3, width: 16, height: 16, background: value ? "var(--green)" : "rgba(201,168,76,0.55)" }}
        />
      </button>
    </div>
  </div>
);

/* ─────────────────────────────────────
   COMPLETION CHIPS
───────────────────────────────────── */
const CompletionChips = ({ steps, completed }) => (
  <div className="flex flex-wrap gap-2">
    {steps.map((s) => {
      const done = completed.has(s.key);
      return (
        <motion.div
          key={s.key}
          animate={{ opacity: done ? 1 : 0.28 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-1.5"
          style={{
            padding: "5px 12px",
            background: done ? "rgba(201,168,76,0.07)" : "transparent",
            border: `1px solid ${done ? "var(--gold-brd)" : "var(--line)"}`,
            borderRadius: 999,
            transition: "background 0.3s, border-color 0.3s",
          }}
        >
          {done
            ? <FiCheck size={8} style={{ color: "var(--gold)" }} />
            : <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--ink-3)" }} />
          }
          <span style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, color: done ? "var(--gold-dim)" : "var(--ink-3)" }}>
            {s.label.split(" ")[0]}
          </span>
        </motion.div>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════ */
const AdminAgenteIA = () => {
  const [form,          setForm]          = useState(initialForm);
  const [isAuth,        setIsAuth]        = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword,  setShowPassword]  = useState(false);
  const [errorAuth,     setErrorAuth]     = useState("");
  const [showMenu,      setShowMenu]      = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [saveState,     setSaveState]     = useState("idle");
  const [drawerOpen,    setDrawerOpen]    = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [direction,   setDirection]   = useState(1);
  const [completed,   setCompleted]   = useState(new Set());

  const totalSteps = STEPS.length;
  const step       = STEPS[currentStep];
  const isLast     = currentStep === totalSteps - 1;
  const isFirst    = currentStep === 0;

  useEffect(() => {
    if (localStorage.getItem("admin_auth") === "true") setIsAuth(true);
  }, []);

  const handleLogin = () => {
    if (passwordInput === PASSWORD) {
      setIsAuth(true);
      localStorage.setItem("admin_auth", "true");
      setErrorAuth("");
    } else {
      setErrorAuth("Contraseña incorrecta");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    setIsAuth(false);
  };

  const fetchAgent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setForm(res.data[0]);
        const done = new Set();
        STEPS.forEach((s) => { if (res.data[0][s.key]) done.add(s.key); });
        setCompleted(done);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { if (isAuth) fetchAgent(); }, [isAuth, fetchAgent]);

  const handleChange  = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const markCompleted = (key) => setCompleted((prev) => new Set([...prev, key]));

  const goNext = () => {
    if (form[step.key]?.trim()) markCompleted(step.key);
    if (!isLast) { setDirection(1); setCurrentStep((p) => p + 1); }
  };

  const goPrev = () => {
    if (!isFirst) { setDirection(-1); setCurrentStep((p) => p - 1); }
  };

  const jumpTo = (i) => {
    setDirection(i > currentStep ? 1 : -1);
    setCurrentStep(i);
  };

  const handleSave = async () => {
    setSaveState("saving");
    try {
      await axios.put(`${API}?id=${form.id}`, form);
      setSaveState("success");
      setCompleted(new Set(STEPS.map((s) => s.key)));
      setTimeout(() => setSaveState("idle"), 2800);
    } catch {
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 2800);
    }
  };

  const saveBtnMap = {
    idle:    { bg: "var(--gold)",           text: "#0A0906",    label: "Guardar configuración", icon: <FiSave size={14} /> },
    saving:  { bg: "rgba(201,168,76,0.35)", text: "var(--ink)", label: "Guardando...",          icon: <FiRefreshCw size={14} className="adm-spin" /> },
    success: { bg: "#3D6B4F",              text: "var(--ink)", label: "¡Agente actualizado!", icon: <FiCheck size={14} /> },
    error:   { bg: "#7B3B3B",              text: "var(--ink)", label: "Error al guardar",      icon: <FiAlertCircle size={14} /> },
  };
  const saveBtn = saveBtnMap[saveState];

  const requiredSteps   = STEPS.filter((s) => s.required);
  const requiredDone    = requiredSteps.filter((s) => completed.has(s.key)).length;
  const allRequiredDone = requiredDone === requiredSteps.length;

  useEffect(() => {
    const handler = (e) => {
      if (!isAuth || loading || drawerOpen) return;
      if (e.key === "ArrowRight" && !isLast)  goNext();
      if (e.key === "ArrowLeft"  && !isFirst) goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isAuth, loading, drawerOpen, currentStep, isLast, isFirst]);

  return (
    <>
      <GlobalStyles />

      {/* ══════════════════════════════════════
          PÁGINA — SIN AtomBackground aquí.
          Solo lo usamos en el overlay de login.
      ══════════════════════════════════════ */}
      <div
        className="relative min-h-screen overflow-x-hidden"
        style={{ background: "var(--bg)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {/* Radial glow top-left — decoración ligera sin canvas */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(201,168,76,0.07) 0%, transparent 55%)" }}
        />

        {/* Top line dorada */}
        <div
          className="fixed top-0 left-0 right-0 h-px z-50"
          style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }}
        />

        {/* ─────── LOGIN ─────── */}
        <AnimatePresence>
          {!isAuth && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] flex items-center justify-center px-4"
            >
              <div
                className="absolute inset-0"
                style={{ background: "rgba(10,9,6,0.96)", backdropFilter: "blur(14px)" }}
              />

              {/*
                AtomBackground SOLO en el overlay del login.
                El contenedor tiene position:absolute + inset-0, lo que le da
                dimensiones reales al canvas para que ResizeObserver funcione.
              */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <AtomBackground />
              </div>

              <motion.div
                initial={{ scale: 0.88, y: 28, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.88, y: 28, opacity: 0 }}
                transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 w-full max-w-sm shadow-2xl"
                style={{ background: "var(--bg-3)", border: "1px solid var(--line)" }}
              >
                <div
                  className="absolute top-0 left-8 right-8 h-px"
                  style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }}
                />

                <div className="p-8 sm:p-10">
                  <div className="flex items-center gap-4 mb-8">
                    <div
                      className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                      style={{ border: "1px solid var(--line)", background: "var(--gold-sub)", color: "var(--gold)" }}
                    >
                      <FiLock size={16} />
                    </div>
                    <div>
                      <SectionLabel>Acceso restringido</SectionLabel>
                      <h2
                        className="mt-1"
                        style={{ fontSize: "clamp(1rem,3vw,1.2rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.01em" }}
                      >
                        IDM Medicina Admin
                      </h2>
                    </div>
                  </div>

                  <div className="h-px mb-6" style={{ background: "var(--line)" }} />

                  <div className="relative mb-3">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                      placeholder="Contraseña de acceso"
                      style={{ background: "var(--bg-2)", color: "var(--ink)", border: "1px solid var(--line)", outline: "none", width: "100%", height: 48, padding: "0 48px 0 16px", fontSize: 14, fontWeight: 300, borderRadius: 0 }}
                    />
                    <button
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2"
                      style={{ background: "none", border: "none", color: "var(--gold-dim)", cursor: "pointer" }}
                    >
                      {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>

                  <AnimatePresence>
                    {errorAuth && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="flex items-center gap-2 mb-3"
                        style={{ fontSize: 12, color: "var(--red)" }}
                      >
                        <FiAlertCircle size={11} /> {errorAuth}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <motion.button
                    whileHover={{ opacity: 0.86 }} whileTap={{ scale: 0.97 }}
                    onClick={handleLogin}
                    className="w-full"
                    style={{ padding: "14px 0", background: "var(--gold)", color: "#0A0906", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", border: "none", cursor: "pointer" }}
                  >
                    Ingresar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {isAuth && <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />}

        {isAuth && (
          <MobileStepDrawer
            steps={STEPS} current={currentStep} completed={completed}
            onJump={jumpTo} open={drawerOpen} onClose={() => setDrawerOpen(false)}
          />
        )}

        {/* ─────── PANEL PRINCIPAL ─────── */}
        {isAuth && (
          <div
            className="relative z-10 w-full max-w-6xl mx-auto"
            style={{ padding: "clamp(80px,10vw,120px) clamp(16px,5vw,48px) 96px" }}
          >
            {/* TOPBAR MOBILE */}
            <div className="flex xl:hidden items-center justify-between mb-6 show-mobile-header">
              <button
                onClick={() => setDrawerOpen(true)}
                className="flex items-center gap-2"
                style={{ padding: "8px 14px", background: "var(--gold-sub)", border: "1px solid var(--gold-brd)", color: "var(--gold)", cursor: "pointer", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase" }}
              >
                <FiMenu size={13} />
                Pasos
                <span style={{ color: "var(--ink-3)", fontWeight: 300 }}>{currentStep + 1}/{totalSteps}</span>
              </button>
              <div className="flex gap-2">
                <button onClick={fetchAgent} className="w-9 h-9 flex items-center justify-center" style={{ background: "transparent", border: "1px solid var(--line)", color: "var(--gold)", cursor: "pointer" }}>
                  <FiRefreshCw size={13} className={loading ? "adm-spin" : ""} />
                </button>
                <button onClick={handleLogout} className="w-9 h-9 flex items-center justify-center" style={{ background: "transparent", border: "1px solid rgba(224,122,122,0.2)", color: "var(--red)", cursor: "pointer" }}>
                  <FiLogOut size={13} />
                </button>
              </div>
            </div>

            {/* HEADER */}
            <motion.div variants={fadeUp} initial="hidden" animate="show" className="mb-12">
              <SectionLabel className="mb-3">Panel de administración</SectionLabel>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <h1 style={{ fontSize: "clamp(1.75rem,4vw,2.8rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.04 }}>
                    Agente <em style={{ color: "var(--gold)", fontStyle: "normal" }}>IA</em> — IDM Medicina
                  </h1>
                  <p className="mt-2" style={{ fontSize: 13, fontWeight: 300, color: "var(--ink-3)", fontStyle: "italic" }}>
                    Configurá cómo responde tu asistente a cada estudiante
                  </p>
                </div>
                <div className="hide-on-mobile flex items-center gap-4">
                  <div>
                    <SectionLabel className="mb-1">Requeridos</SectionLabel>
                    <p style={{ fontSize: 14, fontWeight: 700, color: allRequiredDone ? "var(--green)" : "var(--gold)" }}>
                      {requiredDone} / {requiredSteps.length}
                    </p>
                  </div>
                  <motion.button whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }} onClick={fetchAgent}
                    className="w-10 h-10 flex items-center justify-center" style={{ border: "1px solid var(--line)", color: "var(--gold)", background: "transparent", cursor: "pointer" }}>
                    <FiRefreshCw size={14} className={loading ? "adm-spin" : ""} />
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }} onClick={handleLogout}
                    className="w-10 h-10 flex items-center justify-center" style={{ border: "1px solid rgba(224,122,122,0.2)", color: "var(--red)", background: "transparent", cursor: "pointer" }}>
                    <FiLogOut size={14} />
                  </motion.button>
                </div>
              </div>
              <motion.div
                className="h-px mt-6"
                style={{ background: "linear-gradient(90deg, var(--gold) 0%, transparent 100%)", opacity: 0.14 }}
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              />
            </motion.div>

            {/* LOADING */}
            <AnimatePresence>
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex flex-col items-center py-32 gap-5"
                >
                  <div className="adm-spin" style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid var(--line)", borderTopColor: "var(--gold)" }} />
                  <p style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--gold-dim)" }}>
                    Cargando configuración del agente
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* WIZARD */}
            {!loading && (
              <motion.div variants={fadeUp} initial="hidden" animate="show">

                {/* Progreso */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <p style={{ fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--ink-3)", fontWeight: 600 }}>
                      {step.label}
                    </p>
                    <p style={{ fontSize: 11, color: "var(--gold-dim)", fontWeight: 700 }}>
                      {Math.round(((currentStep + 1) / totalSteps) * 100)}%
                    </p>
                  </div>
                  <ProgressBar current={currentStep} total={totalSteps} />
                </div>

                {/* Layout */}
                <div className="flex gap-10 items-start">
                  <StepSidebar steps={STEPS} current={currentStep} completed={completed} onJump={jumpTo} />

                  <div className="flex-1 min-w-0">

                    {/* Card del paso */}
                    <div
                      className="relative overflow-hidden"
                      style={{ background: "var(--bg-3)", border: "1px solid var(--line)", padding: "clamp(24px,4vw,40px)" }}
                    >
                      <div
                        className="absolute top-0 left-6 right-6 h-px"
                        style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.35), transparent)" }}
                      />

                      <AnimatePresence custom={direction} mode="wait">
                        <motion.div
                          key={step.key}
                          custom={direction}
                          variants={pageVariants}
                          initial="enter" animate="center" exit="exit"
                        >
                          {/* Header */}
                          <div className="flex items-start gap-5 pb-6 mb-6" style={{ borderBottom: "1px solid var(--line)" }}>
                            <motion.div
                              initial={{ scale: 0.65, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                              className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                              style={{ border: "1px solid var(--line)", background: "var(--gold-sub)" }}
                            >
                              {step.icon}
                            </motion.div>
                            <div className="flex-1 min-w-0 pt-1">
                              <div className="flex items-center flex-wrap gap-2">
                                <h2 style={{ fontSize: "clamp(1rem,2.5vw,1.25rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.01em" }}>
                                  {step.label}
                                </h2>
                                <span style={{
                                  fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
                                  padding: "3px 10px", borderRadius: 999,
                                  background: step.required ? "rgba(201,168,76,0.08)" : "rgba(109,191,143,0.09)",
                                  color:      step.required ? "var(--gold)" : "var(--green)",
                                  border:     `1px solid ${step.required ? "var(--gold-brd)" : "rgba(109,191,143,0.22)"}`,
                                }}>
                                  {step.required ? "Requerido" : "Opcional"}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Hint */}
                          <motion.p
                            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            className="mb-6"
                            style={{ fontSize: 13, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.85, borderLeft: "2px solid rgba(201,168,76,0.28)", paddingLeft: 16 }}
                          >
                            {step.hint}
                          </motion.p>

                          {/* Campo */}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.16, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          >
                            <StyledField
                              name={step.key} value={form[step.key]}
                              onChange={handleChange} placeholder={step.placeholder}
                              rows={step.rows} type={step.type}
                            />
                          </motion.div>

                          <p className="text-right mt-2" style={{ fontSize: 10, color: "var(--ink-3)", fontWeight: 300 }}>
                            {(form[step.key] || "").length} caracteres
                          </p>
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* NAVEGACIÓN */}
                    <div className="flex items-center justify-between mt-5 gap-3">
                      <motion.button
                        whileHover={!isFirst ? { x: -3 } : {}} whileTap={!isFirst ? { scale: 0.96 } : {}}
                        onClick={goPrev} disabled={isFirst}
                        className="flex items-center gap-1.5"
                        style={{
                          padding: "10px 18px", background: "transparent",
                          border:  `1px solid ${isFirst ? "transparent" : "var(--line)"}`,
                          color:   isFirst ? "var(--ink-3)" : "var(--ink-2)",
                          cursor:  isFirst ? "default" : "pointer",
                          opacity: isFirst ? 0.3 : 1,
                          fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase",
                        }}
                      >
                        <FiChevronLeft size={12} />
                        <span className="hidden sm:inline">Anterior</span>
                      </motion.button>

                      <div className="hidden sm:flex items-center gap-2">
                        {STEPS.map((s, i) => (
                          <motion.button
                            key={s.key} onClick={() => jumpTo(i)}
                            animate={{ scale: i === currentStep ? 1.6 : 1, opacity: i === currentStep ? 1 : 0.45 }}
                            transition={{ duration: 0.22 }}
                            style={{
                              width: 5, height: 5, borderRadius: "50%", border: "none", cursor: "pointer",
                              background: completed.has(s.key) ? "var(--gold)" : i === currentStep ? "var(--gold-dim)" : "var(--line)",
                            }}
                          />
                        ))}
                      </div>
                      <p className="sm:hidden" style={{ fontSize: 10, color: "var(--ink-3)", fontWeight: 300 }}>
                        {currentStep + 1} / {totalSteps}
                      </p>

                      <motion.button
                        whileHover={{ x: 3 }} whileTap={{ scale: 0.96 }}
                        onClick={goNext}
                        className="flex items-center gap-1.5"
                        style={{ padding: "10px 20px", background: "var(--gold-sub)", color: "var(--gold)", border: "1px solid var(--gold-brd)", cursor: "pointer", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}
                      >
                        {isLast
                          ? <>Finalizar <FiArrowRight size={12} /></>
                          : <><span className="hidden sm:inline">Siguiente</span><span className="sm:hidden">Sig.</span> <FiChevronRight size={12} /></>
                        }
                      </motion.button>
                    </div>

                    {/* SECCIÓN INFERIOR */}
                    <div className="mt-10 flex flex-col gap-5">
                      <motion.div
                        className="h-px"
                        style={{ background: "linear-gradient(90deg, var(--gold) 0%, transparent 100%)", opacity: 0.12 }}
                        initial={{ scaleX: 0, originX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      />

                      <ActiveToggle
                        value={form.activo}
                        onChange={() => setForm((p) => ({ ...p, activo: p.activo ? 0 : 1 }))}
                      />

                      <CompletionChips steps={STEPS} completed={completed} />

                      {/* Botón guardar — sin AtomBackground alrededor */}
                      <motion.button
                        whileHover={{ opacity: saveState === "idle" ? 0.87 : 1 }}
                        whileTap={{ scale: 0.985 }}
                        onClick={handleSave}
                        disabled={saveState === "saving"}
                        className="w-full flex items-center justify-center gap-2.5"
                        style={{ padding: "16px 0", background: saveBtn.bg, color: saveBtn.text, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", transition: "background 0.35s, opacity 0.2s" }}
                      >
                        <AnimatePresence mode="wait">
                          <motion.span key={`icon-${saveState}`}
                            initial={{ opacity: 0, scale: 0.65 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.65 }}
                            transition={{ duration: 0.2 }}>
                            {saveBtn.icon}
                          </motion.span>
                        </AnimatePresence>
                        <AnimatePresence mode="wait">
                          <motion.span key={`label-${saveState}`}
                            initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.22 }}>
                            {saveBtn.label}
                          </motion.span>
                        </AnimatePresence>
                        {saveState === "idle" && !allRequiredDone && (
                          <span style={{ fontSize: 9, letterSpacing: "0.18em", opacity: 0.52, fontWeight: 400 }}>
                            ({requiredDone}/{requiredSteps.length} req.)
                          </span>
                        )}
                      </motion.button>

                      {!allRequiredDone && saveState === "idle" && (
                        <motion.p
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          className="text-center"
                          style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 300 }}
                        >
                          Completá los campos requeridos antes de guardar
                        </motion.p>
                      )}

                      <p className="text-center" style={{ fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--ink-3)", fontWeight: 300 }}>
                        ← → para navegar entre pasos
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AdminAgenteIA;